import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface WebhookRow {
  readonly webhookId: string;
  readonly webhookCode: string;
  readonly eventType: string;
  readonly direction: string;
  readonly targetUrl: string;
  readonly signatureAlgo: string;
  readonly status: string;
  readonly failureCount: number;
}

const STATUSES = ['', 'ACTIVE', 'PAUSED', 'DISABLED'];
const DIRECTIONS = ['OUTBOUND', 'INBOUND'];
const ALGOS = ['HMAC_SHA256', 'HMAC_SHA1', 'NONE'];

/** M29 — Webhook Management (§33 / §21). Lists inbound and outbound webhook
 * registrations with their bound event, signing algorithm, delivery status and
 * failure count (BR-05: failed deliveries are retried), and operates the full
 * webhook lifecycle — register → activate/pause → trigger → delete — each wired
 * to the guarded /v1/eip/webhooks write API. */
export function M29WebhookManagementPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<WebhookRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [eventType, setEventType] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [direction, setDirection] = useState('OUTBOUND');
  const [signatureAlgo, setSignatureAlgo] = useState('HMAC_SHA256');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = status ? `?status=${status}` : '';
    apiRequest<{ data: WebhookRow[] }>(`/v1/eip/webhooks${qs}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m29.webhook.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!eventType.trim()) { setActionError(t('m29.webhook.validationEventType')); return; }
    if (!targetUrl.trim()) { setActionError(t('m29.webhook.validationTargetUrl')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: WebhookRow }>(`/v1/eip/webhooks`, {
        method: 'POST',
        body: { eventType: eventType.trim(), targetUrl: targetUrl.trim(), direction, signatureAlgo },
        token,
      });
      setNotice(t('m29.webhook.noticeRegistered', { code: r.data.webhookCode }));
      setEventType('');
      setTargetUrl('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, t('m29.webhook.verbRegister'), 'M29_WEBHOOK_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const activate = (w: WebhookRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/webhooks/${w.webhookId}/activate`, { method: 'POST', body: {}, token }),
      t('m29.webhook.noticeActivated', { code: w.webhookCode }), t('m29.webhook.verbActivate'), 'M29_WEBHOOK_MANAGE',
    );

  const pause = (w: WebhookRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/webhooks/${w.webhookId}/pause`, { method: 'POST', body: {}, token }),
      t('m29.webhook.noticePaused', { code: w.webhookCode }), t('m29.webhook.verbPause'), 'M29_WEBHOOK_MANAGE',
    );

  const trigger = (w: WebhookRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/webhooks/${w.webhookId}/trigger`, { method: 'POST', body: { payload: {} }, token }),
      t('m29.webhook.noticeTriggered', { code: w.webhookCode }), t('m29.webhook.verbTrigger'), 'M29_WEBHOOK_MANAGE',
    );

  const remove = (w: WebhookRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/webhooks/${w.webhookId}`, { method: 'DELETE', token }),
      t('m29.webhook.noticeDeleted', { code: w.webhookCode }), t('m29.webhook.verbDelete'), 'M29_WEBHOOK_MANAGE',
    );

  const failing = rows.filter((w) => w.failureCount > 0).length;

  return (
    <main aria-label="webhook-management">
      <h1>{t('m29.webhook.title')}</h1>

      <section aria-label="create-webhook">
        <h2>{t('m29.webhook.registerHeading')}</h2>
        <form aria-label="webhook-form" onSubmit={(e) => void create(e)}>
          <label>{t('m29.webhook.fieldEventType')}<input aria-label="field-event-type" value={eventType} onChange={(e) => setEventType(e.target.value)} /></label>
          <label>{t('m29.webhook.fieldTargetUrl')}<input aria-label="field-target-url" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} /></label>
          <label>{t('m29.webhook.fieldDirection')}
            <select aria-label="field-direction" value={direction} onChange={(e) => setDirection(e.target.value)}>
              {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label>{t('m29.webhook.fieldAlgorithm')}
            <select aria-label="field-signature-algo" value={signatureAlgo} onChange={(e) => setSignatureAlgo(e.target.value)}>
              {ALGOS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-webhook" disabled={busy}>{busy ? t('m29.webhook.registering') : t('m29.webhook.register')}</button>
        </form>
        {actionError && <p role="alert" aria-label="webhook-error">{actionError}</p>}
        {notice && <p aria-label="webhook-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m29.webhook.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m29.webhook.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m29.webhook.loading')}</p> : (
        <>
          <p aria-label="webhook-count">{t('m29.webhook.count', { count: rows.length })}</p>
          <p aria-label="webhook-failing">{t('m29.webhook.failing', { count: failing })}</p>
          <table aria-label="webhook-table">
            <thead><tr><th>{t('m29.webhook.colCode')}</th><th>{t('m29.webhook.colEvent')}</th><th>{t('m29.webhook.colDirection')}</th><th>{t('m29.webhook.colTargetUrl')}</th><th>{t('m29.webhook.colAlgorithm')}</th><th>{t('m29.webhook.colStatus')}</th><th>{t('m29.webhook.colFailures')}</th><th>{t('m29.webhook.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.webhookId}>
                  <td>{w.webhookCode}</td><td>{w.eventType}</td><td>{w.direction}</td>
                  <td>{w.targetUrl}</td><td>{w.signatureAlgo}</td><td>{w.status}</td><td>{w.failureCount}</td>
                  <td>
                    <button type="button" aria-label={`activate-${w.webhookCode}`} onClick={() => void activate(w)}>{t('m29.webhook.btnActivate')}</button>
                    <button type="button" aria-label={`pause-${w.webhookCode}`} onClick={() => void pause(w)}>{t('m29.webhook.btnPause')}</button>
                    <button type="button" aria-label={`trigger-${w.webhookCode}`} onClick={() => void trigger(w)}>{t('m29.webhook.btnTrigger')}</button>
                    <button type="button" aria-label={`delete-${w.webhookCode}`} onClick={() => void remove(w)}>{t('m29.webhook.btnDelete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
